import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import {
  Box,
  Grid,
  Stack,
  IconButton,
  Switch,
  Paper,
  Select,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  SelectChangeEvent,
  Divider,
  TextField,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { isEmpty } from 'lodash-es';
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  ICalendarAccount,
  ICandidate,
  IOutlookCalendarAccount,
  createCalendarEvent,
  createOutlookCalendarEvent,
  getCalendarAccounts,
  getOutlookCalendarAccounts,
  IOutlookCalendarEvent,
  ICalendarEvent,
  getEvaluationTemplatesByJob,
  emailEvaluationToAttendees,
  getInvitedUsers,
  updateGoogleCalendarEvent,
  updateOutlookCalendarEvent,
} from 'wisestep-api';
import {
  ControlledTextInput,
  ControlledTextArea,
  WsSelect,
  WsButton,
  WsTypography,
  WsIconText,
  WsTypoColors,
  WsDialog,
  GoogleMeetIcon,
  MicrosoftTeamsIcon,
  WsTypoVariants,
  WsDatePicker,
  WsChip,
  WsChipVariants,
  getStartAndEndTime,
  WsColors,
} from 'wisestep-base';

import useCopyToClipboard from '../../features/candidates/talentpool/hooks/useCopyToClipboard';
import { getTimezones } from '../../features/emailSettings/util/TimezoneHelper';
import { useFetch } from '../../hooks/useFetch';
import { success, failed, warning } from '../../utils/NotificationMessages';
import { useNotificationStore } from '../../utils/NotificationStore';
import { TimePickerControlled } from '../TimePickerControlled';

interface ICalendarEventProps {
  candidate?: ICandidate | null;
  defaultValues?: any;
  onClose: () => void;
  onDelete?: (eventInfo?: { eventId: string; selectedMeeting: string }) => Promise<void>;
  onSave: () => void;
  fullWidth?: boolean;
  calendarType?: 'GOOGLE' | 'OUTLOOK';
}

export const VideoConferenceOptions = {
  GOOGLE_MEET: 'google',
  TEAMS: 'teams',
};

const StageStatusCalendarEvent = forwardRef(
  (
    {
      candidate,
      onClose,
      defaultValues,
      onDelete,
      onSave,
      fullWidth,
      calendarType,
    }: ICalendarEventProps,
    ref
  ) => {
    const googleEvent = !calendarType || calendarType === 'GOOGLE';
    const outlookEvent = !calendarType || calendarType === 'OUTLOOK';
    const navigate = useNavigate();
    const setNotification = useNotificationStore((state) => state.setNotification);
    const [, setDisableSubmit] = useState<boolean>(false);
    const [googleCalender, setGoogleCalender] = useState(false);
    const [openGoogleFailure, setOpenGoogleFailure] = useState(false);
    const [googleAccount, setGoogleAccount] = useState<ICalendarAccount | null>();
    const [outlookCalendarAccount, setOutlookCalendarAccount] =
      useState<IOutlookCalendarAccount | null>();
    const [isLoading, setIsLoading] = useState(false);
    const account = googleAccount;
    const oAccount = outlookCalendarAccount;
    const [microsoftCalender, setMicrosoftCalender] = useState(false);
    const [openMicrosoftFailure, setOpenMicrosoftFailure] = useState(false);
    const calendarMeetingsObj:
      | Record<string, { label: string; value: string; icon: JSX.Element }>
      | Record<string, never> = useMemo(() => {
      const obj = {};
      if (googleCalender || defaultValues?.entityType === VideoConferenceOptions.GOOGLE_MEET)
        obj[VideoConferenceOptions.GOOGLE_MEET] = {
          label: 'Google Meet',
          value: VideoConferenceOptions.GOOGLE_MEET,
          icon: <GoogleMeetIcon />,
        };
      if (microsoftCalender || defaultValues?.entityType === VideoConferenceOptions.TEAMS)
        obj[VideoConferenceOptions.TEAMS] = {
          label: 'Teams',
          value: VideoConferenceOptions.TEAMS,
          icon: <MicrosoftTeamsIcon />,
        };
      return obj;
    }, [defaultValues?.entityType, googleCalender, microsoftCalender]);
    const [createdMeetingEvent, setCreatedMeetingEvent] = useState<
      ICalendarEvent | IOutlookCalendarEvent
    >();
    const [isMeetingCreated, setIsMeetingCreated] = useState<boolean>(false);
    const [, copy] = useCopyToClipboard();
    const [templates] = useFetch<any[]>(() => getEvaluationTemplatesByJob(candidate?.jobId ?? ''));
    const [attendees, setAttendees] = useState<any[]>(
      defaultValues?.attendees?.filter(({ email }) => email !== candidate?.email) ?? []
    );

    const generateEventWithConference = async (
      payload: ICalendarEvent | IOutlookCalendarEvent,
      conferenceOption
    ) => {
      const googleConference = conferenceOption === 'google';

      try {
        const response = googleConference
          ? await createCalendarEvent({
              ...(payload as ICalendarEvent),
              meeting: conferenceOption,
              googleOAuth: googleAccount ?? null,
            })
          : await createOutlookCalendarEvent({
              ...payload,
              meeting: conferenceOption,
              outlookOAuthToken: outlookCalendarAccount ?? null,
            });
        const createdEvent: ICalendarEvent = response.data;
        setNotification(success('Conference link generated!'));
        return createdEvent;
      } catch (e) {
        setNotification(failed('Some error occurred. Please try later'));
      }
    };

    const onSubmit = async (data: FieldValues) => {
      const date = watch('date');
      const startTime = watch('startTime');
      const endTime = watch('endTime');
      if (date == undefined || date == null) {
        setNotification(warning('Please select Date'));
        return;
      }
      if (startTime == undefined || startTime == null) {
        setNotification(warning('Please select Start Time'));
        return;
      }
      if (endTime == undefined || endTime == null) {
        setNotification(warning('Please select End Time'));
        return;
      }
      setIsLoading(true);
      const evalTemplateId = methods.getValues('evalTemplateId');
      let viewInviteId, evalInviteId;
      if (evalTemplateId) {
        const canView = attendees
          .filter((a) => a.canView && !a.canEvaluate)
          .map(({ email }) => ({ email, name: '' }));
        const canEvaluate = attendees
          .filter((a) => a.canEvaluate)
          .map(({ email }) => ({ email, name: '' }));
        const subject = `Evaluation for ${candidate?.mappedJobTitle} || ${candidate?.name}`;
        const sharedCandidateName = candidate?.name ?? '';
        const sharedJobTitle = candidate?.mappedJobTitle ?? '';
        if (canView.length) {
          const viewResp = await emailEvaluationToAttendees({
            to: canView,
            subject,
            sharedCandidateName,
            sharedJobTitle,
            body: '',
            interactionId: candidate?.id ?? '',
            jobId: candidate?.jobId ?? '',
            evalTemplateId: null,
          });
          viewInviteId = viewResp.data.invitationId;
        }
        if (canEvaluate.length) {
          const evalResp = await emailEvaluationToAttendees({
            to: canEvaluate,
            subject,
            sharedCandidateName,
            sharedJobTitle,
            body: '',
            interactionId: candidate?.id ?? '',
            jobId: candidate?.jobId ?? '',
            evalTemplateId,
          });
          evalInviteId = evalResp.data.invitationId;
        }
      }
      const payload = {
        ...getPayload(data),
        viewInviteId,
        evalInviteId,
        evalTemplateId,
        jobCandidateInteractionId: candidate?.id,
      };
      try {
        if (googleCalender && googleEvent) {
          if (defaultValues?.eventId) {
            await updateGoogleCalendarEvent({ ...payload, googleOAuth: googleAccount ?? null });
          } else {
            await createCalendarEvent({
              ...payload,
              ...(isMeetingCreated &&
                selectedMeeting !== VideoConferenceOptions.GOOGLE_MEET && { eventId: undefined }),
              googleOAuth: googleAccount ?? null,
            });
          }
        }
        if (microsoftCalender && outlookEvent) {
          if (defaultValues?.eventId) {
            await updateOutlookCalendarEvent({
              ...payload,
              outlookOAuthToken: outlookCalendarAccount ?? null,
            });
          } else {
            await createOutlookCalendarEvent({
              ...payload,
              outlookOAuthToken: outlookCalendarAccount ?? null,
              ...(isMeetingCreated &&
                selectedMeeting === VideoConferenceOptions.GOOGLE_MEET && { eventId: undefined }),
            });
          }
        }
        if (defaultValues) setNotification(success('Event updated in calendar'));
        else setNotification(success('Event created in calendar'));
        onSave();
        onClose();
      } catch (e) {
        setDisableSubmit(false);
        setNotification(failed('Some error occurred. Please try later'));
      }
      setIsLoading(false);
    };

    const sendRemainder = (event: React.ChangeEvent<HTMLInputElement>) =>
      setValue('sendEmail', event.target.checked);

    const checkGoogleCalender = async () => {
      if (!googleCalender) {
        const req = await getCalendarAccounts();
        if (req?.data && req.data.length > 0) {
          setGoogleAccount(req.data[0]);
        } else {
          setOpenGoogleFailure(!googleCalender);
          setGoogleAccount(null);
        }
      }
      setGoogleCalender(!googleCalender);
    };

    const checkMicrosoftCalender = async () => {
      if (!microsoftCalender) {
        const req = await getOutlookCalendarAccounts();
        if (req?.data && req.data.length > 0) {
          setOutlookCalendarAccount(req.data[0]);
        } else {
          setOpenMicrosoftFailure(!microsoftCalender);
          setOutlookCalendarAccount(null);
        }
      }
      setMicrosoftCalender(!microsoftCalender);
    };

    const redirectToGoogleCalenderSettings = () => {
      window.location.href = '/calender-settings';
    };

    const redirectToMicrosoftCalenderSettings = () => {
      window.location.href = '/calender-settings/microsoft';
    };

    const closeGoogleFailure = () => {
      setOpenGoogleFailure(false);
      setGoogleCalender(false);
    };

    const closeMicrosoftFailure = () => {
      setOpenMicrosoftFailure(false);
      setMicrosoftCalender(false);
    };

    const setDefault = () => {
      setGoogleCalender(googleAccount != null && googleAccount.email != '');
      setMicrosoftCalender(outlookCalendarAccount != null && outlookCalendarAccount.email != '');
    };

    useEffect(setDefault, [googleAccount, outlookCalendarAccount]);
    useEffect(() => {
      if (!isEmpty(defaultValues) && defaultValues?.entityType) {
        setSelectedMeeting(defaultValues.entityType);
        setCreatedMeetingEvent(defaultValues);
        setIsMeetingCreated(true);
      }
    }, [defaultValues]);

    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const methods = useForm<any>({
      defaultValues: {
        title: candidate ? `Call with ${candidate.name}` : '',
        tz: tz === 'Asia/Calcutta' ? 'Asia/Kolkata' : tz,
        from: new Date(),
        to: new Date(new Date().getTime() + 30 * 60000),
        guest: [],
        reminder: 10,
        reminderIn: 'Minutes',
        attendees: [{ canView: true }],
        evalTemplateId: '',
        ...defaultValues,
        ...(defaultValues?.eventId && {
          attendees: defaultValues.attendees?.slice(1),
          date: new Date(defaultValues.from),
          startTime: new Date(defaultValues.from),
          endTime: new Date(defaultValues.to),
          description: defaultValues.eventDescription,
        }),
      },
    });

    const { handleSubmit, setValue, reset, watch, getValues } = methods;
    const [selectedMeeting, setSelectedMeeting] = useState<string>();

    const getPayload = (data) => {
      let reminder = data.reminder;
      if (data.reminderIn == 'Hour' || data.reminderIn == 'hours') {
        reminder = data.reminder * 60;
      }
      const { from, to } = getStartAndEndTime(
        data.date,
        data.startTime,
        data.endTime,
        data.timzone
      );
      return {
        attendees: [candidate?.email, ...attendees.map(({ email }) => email)],
        entityId: candidate?.id ?? '',
        entityType: data.entityType,
        eventDescription: data.description,
        eventSummary: data.title,
        eventId: data.eventId,
        from,
        to,
        location: data.location,
        reminders: [reminder],
        googleOAuth: null,
        tz: data.tz,
      };
    };

    const onChangeOfConferenceOption = async (event: SelectChangeEvent<string>) => {
      if (isMeetingCreated && onDelete) {
        onDelete({
          eventId: createdMeetingEvent?.eventId as string,
          selectedMeeting: selectedMeeting as string,
        });
        reset({ ...watch(), eventId: undefined, description: undefined });
      }
      setSelectedMeeting(event.target.value);
      const conferenceOption = event.target.value;
      setIsMeetingCreated(false);
      const data = methods.getValues();
      const payload = getPayload(data);
      const createEventAndFetch: ICalendarEvent | undefined = await generateEventWithConference(
        payload,
        conferenceOption
      );
      const optionString =
        VideoConferenceOptions.GOOGLE_MEET === conferenceOption ? 'Google' : 'Teams';
      const contentString =
        `Join ${optionString} Video conference` +
        `\nConference Link: ${createEventAndFetch?.meeting}\n`;
      const description =
        data.description ??
        (createEventAndFetch?.eventDescription
          ? contentString + createEventAndFetch?.eventDescription
          : contentString);
      setCreatedMeetingEvent(createEventAndFetch);
      reset({
        ...getValues(),
        attendees: getValues('attendees'),
        title: createEventAndFetch?.eventSummary,
        from: new Date(createEventAndFetch?.from ?? ''),
        to: new Date(createEventAndFetch?.to ?? ''),
        guest:
          createEventAndFetch?.attendees &&
          createEventAndFetch?.attendees.map((attendee) => attendee),
        reminder: 10,
        reminderIn: 'Minutes',
        eventId: createEventAndFetch?.eventId,
        location: createEventAndFetch?.location,
        entityType: createEventAndFetch?.entityType,
        description,
      });
      setIsMeetingCreated(true);
      setDisableSubmit(false);
    };

    useImperativeHandle(ref, () => ({ onSubmit: handleSubmit(onSubmit) }));

    useEffect(() => {
      getCalendarAccounts().then((resp) => setGoogleAccount(resp.data?.[0] ?? null));
      getOutlookCalendarAccounts().then((resp) =>
        setOutlookCalendarAccount(resp.data?.[0] ?? null)
      );
      (async () => {
        let canEvalEmails, canViewEmails;
        if (defaultValues?.evalInviteId) {
          const { data } = await getInvitedUsers(defaultValues?.evalInviteId);
          canEvalEmails = data?.map(({ email }) => email) ?? [];
        }
        if (defaultValues?.viewInviteId) {
          const { data } = await getInvitedUsers(defaultValues?.viewInviteId);
          canViewEmails = data?.map(({ email }) => email) ?? [];
        }
        const mapAttendee = (attendee) => ({
          ...attendee,
          canView: canViewEmails?.includes(attendee.email) ?? false,
          canEvaluate: canEvalEmails?.includes(attendee.email) ?? false,
        });
        setAttendees(attendees.map(mapAttendee));
      })();
    }, []);

    return (
      <FormProvider {...methods}>
        <Stack direction="row" sx={{ flexGrow: 1 }}>
          {!fullWidth && <Divider orientation="vertical" flexItem />}
          {account || oAccount ? (
            <Stack
              component="form"
              onSubmit={handleSubmit((data) => onSubmit(data))}
              justifyContent="space-between"
            >
              <Grid
                sx={{ p: '1.25rem 1.25rem 1.25rem 2.25rem' }}
                container
                spacing={2}
                alignItems="center"
              >
                <Grid item xs={4}>
                  <WsTypography>Event Title</WsTypography>
                </Grid>
                <Grid item xs={8}>
                  <ControlledTextInput
                    name="title"
                    labelText=""
                    fullWidth
                    id="event_title"
                    placeholder="Add a title"
                    variant="outlined"
                    autoComplete="event_title"
                    required
                  />
                </Grid>
                <Grid item xs={4}>
                  <WsTypography>Date*</WsTypography>
                </Grid>
                <Grid item xs={3}>
                  <WsDatePicker name="date" label="Date" size="small" />
                </Grid>
                <Grid item xs={5}>
                  <WsSelect
                    size="small"
                    placeholder="Select Time Zone"
                    name="tz"
                    selectArray={getTimezones().map((val) => ({
                      label: val.label,
                      value: val.tzCode,
                    }))}
                  />
                </Grid>
                <Grid item xs={4}>
                  <WsTypography>Time*</WsTypography>
                </Grid>
                <Grid item xs={3}>
                  <TimePickerControlled name="startTime" placeholder="Start Time" />
                </Grid>
                <Grid item xs={3}>
                  <TimePickerControlled name="endTime" placeholder="End Time" />
                </Grid>
                <Grid item xs={4}>
                  <WsTypography>Candidate</WsTypography>
                </Grid>
                <Grid item xs={8}>
                  <WsChip value={candidate?.email as string} variant={WsChipVariants.PRIMARY} />
                </Grid>
                <Grid item xs={4}>
                  <WsTypography>Attendees</WsTypography>
                </Grid>
                <Grid item xs={8}>
                  <Stack spacing={1} alignItems="flex-start">
                    {attendees.map((a, i) => (
                      <Stack key={i} direction="row" spacing={1} width="100%">
                        <TextField
                          fullWidth
                          value={attendees[i].email ?? ''}
                          onChange={(e) =>
                            setAttendees(
                              attendees.map((attendee, index) =>
                                index === i ? { ...attendee, email: e.target.value } : attendee
                              )
                            )
                          }
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={attendees[i].canView ?? true}
                              onChange={(e) =>
                                setAttendees(
                                  attendees.map((attendee, index) =>
                                    index === i
                                      ? { ...attendee, canView: e.target.checked }
                                      : attendee
                                  )
                                )
                              }
                            />
                          }
                          label="View"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={attendees[i].canEvaluate ?? false}
                              onChange={(e) =>
                                setAttendees(
                                  attendees.map((attendee, index) =>
                                    index === i
                                      ? { ...attendee, canEvaluate: e.target.checked }
                                      : attendee
                                  )
                                )
                              }
                            />
                          }
                          label="Evaluate"
                        />
                        <IconButton
                          onClick={() => {
                            attendees.splice(i, 1);
                            setAttendees([...attendees]);
                          }}
                        >
                          <DeleteOutlineOutlinedIcon sx={{ color: 'error.main' }} />
                        </IconButton>
                      </Stack>
                    ))}
                    <WsButton
                      startIcon={<AddIcon />}
                      variant="outlined"
                      onClick={() => setAttendees([...attendees, { canView: true }])}
                    >
                      Add Attendee
                    </WsButton>
                  </Stack>
                </Grid>
                <Grid item xs={4}>
                  <WsTypography>Select Evaluation</WsTypography>
                </Grid>
                <Grid item xs={8}>
                  <WsSelect
                    placeholder="Select Option"
                    name="evalTemplateId"
                    selectArray={
                      templates?.map(({ templateId, templateName }) => ({
                        label: templateName,
                        value: templateId,
                      })) ?? []
                    }
                    size="small"
                  />
                </Grid>
                <Grid item xs={4}>
                  <WsTypography>Location</WsTypography>
                </Grid>
                <Grid item xs={8}>
                  <ControlledTextInput
                    name="location"
                    labelText=""
                    fullWidth
                    id="event_title"
                    placeholder="Add location"
                    variant="outlined"
                    autoComplete="event_title"
                  />
                </Grid>
                <Grid item xs={4}>
                  <WsTypography>Description</WsTypography>
                </Grid>
                <Grid item xs={8}>
                  <ControlledTextArea name="description" />
                </Grid>
                <Grid item xs={4}>
                  <WsTypography>Notify Attendees</WsTypography>
                </Grid>
                <Grid item xs={4}>
                  <ControlledTextInput
                    name="reminder"
                    labelText=""
                    type="number"
                    fullWidth
                    id="event_title"
                    placeholder="Add reminder"
                    variant="outlined"
                    autoComplete="event_title"
                    required
                  />
                </Grid>
                <Grid item xs={4}>
                  <WsSelect
                    name="reminderIn"
                    label=""
                    selectArray={[
                      { label: 'Hour', value: 'Hour' },
                      { label: 'Minutes', value: 'Minutes' },
                    ]}
                    defaultValue="Minutes"
                    placeholder=""
                  />
                </Grid>
                <Grid item xs={4}></Grid>
                <Grid item xs={7}>
                  <Select
                    value={selectedMeeting}
                    defaultValue={
                      defaultValues && defaultValues.entityType
                        ? defaultValues.entityType
                        : undefined
                    }
                    onChange={onChangeOfConferenceOption}
                    displayEmpty
                    fullWidth
                    style={{ height: '2.5rem', fontWeight: 'bold' }}
                    renderValue={(value) =>
                      isEmpty(value) ? (
                        <WsTypography color={WsTypoColors.PRIMARY600} bold cursor="pointer">
                          Setup a google or teams meeting
                        </WsTypography>
                      ) : (
                        <Grid container columnSpacing={2} alignItems="center">
                          <Grid item xs={2} pt="0.3rem">
                            {calendarMeetingsObj[value ?? ''].icon}
                          </Grid>
                          <Grid item xs={8} overflow="hidden">
                            <WsTypography variant={WsTypoVariants.BODY2}>
                              {isMeetingCreated
                                ? createdMeetingEvent?.meeting ?? `Join ${value} meeting`
                                : calendarMeetingsObj[value ?? ''].label}
                            </WsTypography>
                          </Grid>
                          <Grid item container xs={2}>
                            {!isMeetingCreated && <CircularProgress size="1rem" color="primary" />}
                          </Grid>
                        </Grid>
                      )
                    }
                  >
                    {Object.values(calendarMeetingsObj).map((meeting) => (
                      <MenuItem key={meeting.value} value={meeting.value}>
                        <ListItemIcon>{meeting.icon}</ListItemIcon>
                        <ListItemText>
                          <WsTypography cursor="pointer">{meeting.label}</WsTypography>
                        </ListItemText>
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item xs={1}>
                  {isMeetingCreated && (
                    <IconButton onClick={() => copy(createdMeetingEvent?.meeting ?? '')}>
                      <ContentCopyIcon color="primary" fontSize="small" />
                    </IconButton>
                  )}
                </Grid>
                <Grid item xs={9} py={1}>
                  <WsIconText
                    icon={<Switch onChange={sendRemainder} defaultChecked />}
                    text={'Notify by email'}
                  />
                </Grid>
                {googleEvent && (
                  <>
                    <Grid item container xs={5} py={1}>
                      <WsIconText
                        icon={<Switch onChange={checkGoogleCalender} checked={googleCalender} />}
                        text={'Add to Google Calender'}
                      />
                    </Grid>
                    <Grid item xs={7} py={1}>
                      {googleAccount && googleCalender ? (
                        <WsTypography color={WsTypoColors.PRIMARY}>
                          {googleAccount.email}
                        </WsTypography>
                      ) : googleAccount ? (
                        <></>
                      ) : (
                        <WsButton
                          size="medium"
                          variant="text"
                          onClick={() => setOpenMicrosoftFailure(true)}
                        >
                          Configure Now
                        </WsButton>
                      )}
                    </Grid>
                  </>
                )}
                {outlookEvent && (
                  <>
                    <Grid item container xs={5} py={1}>
                      <WsIconText
                        icon={
                          <Switch onChange={checkMicrosoftCalender} checked={microsoftCalender} />
                        }
                        text={'Add to Microsoft Calender'}
                      />
                    </Grid>
                    <Grid item xs={7} py={1}>
                      {outlookCalendarAccount && microsoftCalender && outlookEvent ? (
                        <WsTypography color={WsTypoColors.PRIMARY}>
                          {outlookCalendarAccount.email}
                        </WsTypography>
                      ) : outlookCalendarAccount ? (
                        <></>
                      ) : (
                        <WsButton
                          size="medium"
                          variant="text"
                          onClick={() => setOpenGoogleFailure(true)}
                        >
                          Configure Now
                        </WsButton>
                      )}
                    </Grid>
                  </>
                )}
              </Grid>
              {fullWidth && (
                <Box
                  sx={{
                    position: 'sticky',
                    bottom: '0',
                    background: WsColors.TertiaryShades50,
                    p: 2.5,
                    minWidth: '100%',
                  }}
                >
                  <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                    <WsButton variant="text" onClick={onClose}>
                      Cancel
                    </WsButton>
                    <WsButton
                      variant="contained"
                      type="submit"
                      disabled={isLoading}
                      endIcon={isLoading ? <CircularProgress size={17} color="secondary" /> : <></>}
                    >
                      {isLoading ? 'Saving...' : 'Save'}
                    </WsButton>
                  </Stack>
                </Box>
              )}
            </Stack>
          ) : account === null && oAccount === null ? (
            <Grid container>
              <Grid mt={1} mb={2} container justifyContent="center">
                <Stack direction="column" spacing={2}>
                  <WsTypography>
                    No account is configured, Please configure your account to set up a meeting
                  </WsTypography>
                  <Grid item xs={12} display="flex" justifyContent="end" py={2}>
                    <Box>
                      <WsButton onClick={() => navigate('/calender-settings')}>
                        Configure gmail
                      </WsButton>
                    </Box>
                    <Box pl={0.5}>
                      <WsButton onClick={() => navigate('/calender-settings/microsoft')}>
                        Configure outlook
                      </WsButton>
                    </Box>
                  </Grid>
                </Stack>
              </Grid>
            </Grid>
          ) : null}

          <WsDialog openDialog={openGoogleFailure}>
            <Paper variant="outlined" sx={{ borderRadius: 2, p: 2 }} role="presentation">
              <Box py={1}>
                <WsTypography>Please configure your email to set up a Google meeting.</WsTypography>
              </Box>
              <Box py={1} pl={0.5}>
                <WsButton
                  size="medium"
                  variant="contained"
                  onClick={redirectToGoogleCalenderSettings}
                >
                  Configure Now
                </WsButton>
                <WsButton size="medium" variant="text" onClick={closeGoogleFailure}>
                  Skip
                </WsButton>
              </Box>
            </Paper>
          </WsDialog>
          <WsDialog openDialog={openMicrosoftFailure}>
            <Paper variant="outlined" sx={{ borderRadius: 2, p: 2 }} role="presentation">
              <Box py={1} pl={0.5}>
                <WsTypography>
                  Please configure your email to set up a Microsoft meeting.
                </WsTypography>
              </Box>
              <Box py={1} pl={0.5}>
                <WsButton
                  size="medium"
                  variant="contained"
                  onClick={redirectToMicrosoftCalenderSettings}
                >
                  Configure Now
                </WsButton>
                <WsButton size="medium" variant="text" onClick={closeMicrosoftFailure}>
                  Skip
                </WsButton>
              </Box>
            </Paper>
          </WsDialog>
        </Stack>
      </FormProvider>
    );
  }
);

StageStatusCalendarEvent.displayName = 'StageStatusCalendarEvent';

export default StageStatusCalendarEvent;
